
import { type Call } from '@twilio/voice-sdk';
import type { CallState, UpdateCallStateAction } from '@/hooks/useTwilioCallState';
import type { TwilioCallTimers } from '@/hooks/useTwilioCallTimers';
import type { UseTwilioCallLogsReturn } from '@/hooks/useTwilioCallLogs';
import { getCurrentCall, clearCurrentCall } from '@/services/twilio/voiceService';

interface ProcessCallStatusChangeParams {
  newStatus: string;
  twilioCall: Call;
  localCallSidRef: React.MutableRefObject<string | null>;
  callLogIdRef: React.MutableRefObject<number | null>;
  currentDuration: number;
  callStateActions: {
    updateCallState: UpdateCallStateAction;
    endCallState: () => void;
    resetCallState: () => void;
  };
  callTimers: Pick<TwilioCallTimers, 'startDurationTimer' | 'stopPolling'>;
  callLogActions: Pick<UseTwilioCallLogsReturn, 'finalizeCallLogInDb' | 'updateCallProgress'>;
}

export const processCallStatusChange = ({
  newStatus,
  twilioCall,
  localCallSidRef,
  callLogIdRef,
  currentDuration,
  callStateActions,
  callTimers,
  callLogActions,
}: ProcessCallStatusChangeParams): void => {
  const currentTwilioCallSid = twilioCall.parameters.CallSid;
  const { updateCallState, endCallState } = callStateActions;
  const { startDurationTimer, stopPolling } = callTimers;
  const { finalizeCallLogInDb, updateCallProgress } = callLogActions;

  console.log(`[callStatusChangeLogic] 📊 Status changed to: ${newStatus} for SID: ${currentTwilioCallSid}`);
  
  // Map Twilio SDK status to our internal status
  const internalStatus = mapTwilioStatus(newStatus);
  const isActiveCall = ['connecting', 'ringing', 'open'].includes(internalStatus);
  
  updateCallState({
    callSid: currentTwilioCallSid,
    callStatus: internalStatus,
    callInProgress: isActiveCall,
  });

  // Update call progress in database if we have a log ID
  if (callLogIdRef.current) {
    updateCallProgress(internalStatus, { call_duration: currentDuration });
  }

  // Handle call becoming active (start duration timer)
  if (newStatus === 'open' && currentDuration === 0) {
    console.log('[callStatusChangeLogic] ⏱️ Call opened, starting duration timer');
    startDurationTimer((newDuration) => {
      updateCallState({ duration: newDuration });
      // Update progress every 10 seconds
      if (callLogIdRef.current && newDuration > 0 && newDuration % 10 === 0) {
        updateCallProgress('open', { call_duration: newDuration });
      }
    });
  }

  // Handle call end states
  const endStates = ['closed', 'cancelled', 'failed', 'busy', 'no-answer'];
  if (endStates.includes(internalStatus)) {
    console.log(`[callStatusChangeLogic] 📴 Call ended with status ${internalStatus}. Finalizing for SID: ${currentTwilioCallSid}`);
    
    // Stop timers immediately
    stopPolling();
    
    // Finalize call log
    finalizeCallLogInDb(internalStatus, `Call ended with status: ${newStatus}`, currentDuration)
      .then(() => {
        console.log('[callStatusChangeLogic] ✅ Call log finalized successfully');
      })
      .catch(error => {
        console.error('[callStatusChangeLogic] ❌ Error finalizing call log:', error);
      })
      .finally(() => {
        // End call state (preserve data for UI) instead of full reset
        performCallCleanup(currentTwilioCallSid, localCallSidRef, callLogIdRef, endCallState);
      });
  }
};

// Helper function to map Twilio status to internal status
function mapTwilioStatus(twilioStatus: string): string {
  const statusMap: Record<string, string> = {
    'pending': 'connecting',
    'ringing': 'ringing', 
    'answered': 'open',
    'open': 'open',
    'closed': 'completed',
    'cancelled': 'canceled',
    'canceled': 'canceled',
    'failed': 'failed',
    'busy': 'busy',
    'no-answer': 'no-answer'
  };
  
  return statusMap[twilioStatus] || twilioStatus;
}

// Helper function to perform call cleanup
function performCallCleanup(
  callSid: string,
  localCallSidRef: React.MutableRefObject<string | null>,
  callLogIdRef: React.MutableRefObject<number | null>,
  endCallState: () => void
) {
  console.log(`[callStatusChangeLogic] 🧹 Performing call cleanup for SID: ${callSid}`);
  
  const liveCall = getCurrentCall();
  
  // Only end state if this is our current call or if there's no live call
  if (localCallSidRef.current === callSid || !liveCall) {
    console.log(`[callStatusChangeLogic] ✅ Ending call state. Local SID: ${localCallSidRef.current}, Live call exists: ${!!liveCall}`);
    endCallState();
    clearCurrentCall(); 
    localCallSidRef.current = null;
    callLogIdRef.current = null;
  } else {
    console.log(`[callStatusChangeLogic] ⚠️ Not ending state - different call may be active. Live SID: ${liveCall?.parameters.CallSid}, Local SID: ${localCallSidRef.current}`);
  }
}
